import React, { useCallback, useRef } from 'react';

import { Transition } from 'react-transition-group';

import { defaultTimeout, transitionEasing } from '@core/transitions/constants';
import { getTransitionTimeout } from '@core/transitions/utils';
import type { TransitionTimeout } from '@core/transitions/utils';
import { reflow, useForkRef } from '@core/utils';

type ChildWithRef = React.ReactElement & { ref?: React.Ref<unknown> };

export type Props = {
  /** When true, the child is visible (entered). Default false. */
  in?: boolean;
  /** Single element to fade. When undefined, nothing is rendered. */
  children?: React.ReactElement;
  /**
   * When true, the child performs the enter transition on initial mount when `in` is true.
   * Set to false to skip the enter animation on first mount.
   * @default true
   */
  appear?: boolean;
  mountOnEnter?: boolean;
  /** When true, the child is removed from the DOM after it exits. Default false. */
  unmountOnExit?: boolean;
  /** Duration in ms, or { enter, exit }. Uses defaultTimeout when omitted. */
  timeout?: TransitionTimeout;
  style?: React.CSSProperties;
  /** Called when enter starts. Receives (node, isAppearing). */
  onEnter?: (node: HTMLElement, isAppearing: boolean) => void;
  /** Called when exit has finished. Receives (node). */
  onExited?: (node: HTMLElement) => void;
};

const Fade = (
  props: Props,
  ref: React.Ref<HTMLElement>
): React.ReactElement | null => {
  const {
    appear = true,
    children,
    in: inProp = false,
    mountOnEnter = false,
    onEnter: onEnterProp,
    onExited: onExitedProp,
    style: styleProp,
    timeout = defaultTimeout,
    unmountOnExit = false,
  } = props;

  const nodeRef = useRef<HTMLElement | null>(null);

  const enterDuration = getTransitionTimeout(timeout, 'enter', defaultTimeout);
  const exitDuration = getTransitionTimeout(timeout, 'exit', defaultTimeout);
  const timeoutProp =
    typeof timeout === 'object'
      ? { enter: enterDuration, exit: exitDuration }
      : timeout;

  const handleOwnRef = useCallback(
    (value: React.ReactInstance | HTMLElement | null) => {
      nodeRef.current = value instanceof HTMLElement ? value : null;
    },
    []
  );
  const handleRef = useForkRef(
    useForkRef((children as ChildWithRef).ref, handleOwnRef),
    ref
  );

  const handleEnter = useCallback(
    (isAppearing?: boolean) => {
      const node = nodeRef.current;
      if (node) {
        reflow(node);
        node.style.transition = `opacity ${enterDuration}ms ${transitionEasing.entrance}`;
      }
      if (node) onEnterProp?.(node, isAppearing ?? false);
    },
    [enterDuration, onEnterProp]
  );

  const handleExit = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = `opacity ${exitDuration}ms ${transitionEasing.exit}`;
    }
  }, [exitDuration]);

  const handleExited = useCallback(() => {
    const node = nodeRef.current;
    if (node) {
      node.style.transition = '';
    }
    if (node) onExitedProp?.(node);
  }, [onExitedProp]);

  if (children == null) return null;

  return (
    <Transition
      appear={appear}
      in={inProp}
      mountOnEnter={mountOnEnter}
      nodeRef={nodeRef as React.RefObject<HTMLElement>}
      timeout={timeoutProp}
      unmountOnExit={unmountOnExit}
      onEnter={handleEnter}
      onExit={handleExit}
      onExited={handleExited}
    >
      {(state) =>
        React.cloneElement(children, {
          ref: handleRef,
          style: {
            opacity: state === 'entering' || state === 'entered' ? 1 : 0,
            visibility: state === 'exited' && !inProp ? 'hidden' : undefined,
            ...styleProp,
            ...(children.props as { style?: React.CSSProperties }).style,
          },
        })
      }
    </Transition>
  );
};

export default React.forwardRef<HTMLElement, Props>(Fade);
